import { differenceInDays, differenceInHours, differenceInMinutes, differenceInMonths, differenceInQuarters, differenceInSeconds, differenceInWeeks, differenceInYears, startOfDay, startOfHour, startOfMinute, startOfMonth, startOfQuarter, startOfSecond, startOfWeek, startOfYear, } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { DateTime } from '../../DateTime';
import { ExprError } from '../ExprError';
import { getTimeUnit } from '../ExprTime';
import * as Types from '../../Types';
import { validateArguments } from '../../SignatureValidation';
export var DateDiffFn;
(function (DateDiffFn) {
    DateDiffFn.Name = `DATEDIFF`;
    DateDiffFn.Stage = "SCALAR";
    DateDiffFn.Signature = `DATEDIFF(<unit>, <datetime-from>, <datetime-to>)`;
    function make(callName, args, type, role) {
        return {
            name: DateDiffFn.Name,
            stage: DateDiffFn.Stage,
            data: { type, role },
            value: (...argVals) => {
                const [datePart, startTime, endTime] = argVals;
                const [, startTimeInfo, endTimeInfo] = args;
                if (typeof datePart !== 'string') {
                    throw new Error(`Invalid time unit input for DATEDIFF: ${datePart}`);
                }
                if (startTime == null || endTime == null) {
                    return null;
                }
                if (typeof startTime !== 'number') {
                    throw new Error(`Invalid time input for DATEDIFF: ${startTime}`);
                }
                if (typeof endTime !== 'number') {
                    throw new Error(`Invalid time input for DATEDIFF: ${endTime}`);
                }
                let startTimeMillis = 0;
                switch (getTimeUnit(startTimeInfo)) {
                    case "MILLIS":
                        startTimeMillis = startTime;
                        break;
                    case "MICROS":
                        startTimeMillis = startTime / 1000;
                        break;
                    case "SECOND":
                        startTimeMillis = startTime * 1000;
                        break;
                }
                let endTimeMillis = 0;
                switch (getTimeUnit(endTimeInfo)) {
                    case "MILLIS":
                        endTimeMillis = endTime;
                        break;
                    case "MICROS":
                        endTimeMillis = endTime / 1000;
                        break;
                    case "SECOND":
                        endTimeMillis = endTime * 1000;
                        break;
                }
                let startFn;
                let diffFn;
                switch (datePart.toString().toUpperCase()) {
                    case "YEAR":
                        startFn = startOfYear;
                        diffFn = differenceInYears;
                        break;
                    case "QUARTER":
                        startFn = startOfQuarter;
                        diffFn = differenceInQuarters;
                        break;
                    case "MONTH":
                        startFn = startOfMonth;
                        diffFn = differenceInMonths;
                        break;
                    case "WEEKDAY":
                    case "DAYOFYEAR":
                    case "DAY":
                        startFn = startOfDay;
                        diffFn = differenceInDays;
                        break;
                    case "WEEK":
                        startFn = startOfWeek;
                        diffFn = differenceInWeeks;
                        break;
                    case "HOUR":
                        startFn = startOfHour;
                        diffFn = differenceInHours;
                        break;
                    case "MINUTE":
                        startFn = startOfMinute;
                        diffFn = differenceInMinutes;
                        break;
                    case "SECOND":
                        startFn = startOfSecond;
                        diffFn = differenceInSeconds;
                        break;
                }
                if (!startFn || !diffFn) {
                    throw new Error(`Time unit not supported: ${datePart}`);
                }
                const dateFnsStart = startFn(utcToZonedTime(startTimeMillis, 'Z'));
                const dateFnsEnd = startFn(utcToZonedTime(endTimeMillis, 'Z'));
                return diffFn(dateFnsEnd, dateFnsStart);
            },
        };
    }
    DateDiffFn.make = make;
    function validate(callNode, callName, args) {
        const sig = {
            meta: { name: DateDiffFn.Name, signature: DateDiffFn.Signature, location: callNode.location },
            arguments: [
                {
                    customValidation: (unit) => {
                        const errors = [];
                        if (!(Types.isStringLiteral(unit) && DateTime.unit(unit.value))) {
                            const validUnits = DateTime.validUnits.join(`', '`);
                            const displayValue = Types.isLiteralLike(unit)
                                ? unit.value
                                : Types.getResolvedType(unit);
                            errors.push(new ExprError.InvalidArgument(DateDiffFn.Name, DateDiffFn.Signature, `expects <unit> to be one of '${validUnits}', saw: ` +
                                `<${displayValue}>`, callNode.location));
                        }
                        return errors;
                    },
                },
                {
                    customValidation: (arg) => {
                        switch (Types.getResolvedType(arg)) {
                            case "DATE_TIME":
                            case "TIMESTAMP":
                                return [];
                        }
                        return [
                            new ExprError.InvalidArgument(DateDiffFn.Name, DateDiffFn.Signature, `expected <datetime> to be an instance of either DATETIME or TIMESTAMP but received ${Types.getResolvedType(arg)}`, callNode.location),
                        ];
                    },
                },
                {
                    customValidation: (arg) => {
                        const errors = [];
                        if (arg.stage !== args[1].stage) {
                            if (Types.isAggregate(arg) || Types.isAggregate(args[1])) {
                                errors.push(new ExprError.InvalidArgument(DateDiffFn.Name, DateDiffFn.Signature, `Cannot mix aggregate and non-aggregate arguments with this function`, callNode.location));
                            }
                        }
                        switch (Types.getResolvedType(arg)) {
                            case "DATE_TIME":
                            case "TIMESTAMP":
                                break;
                            default:
                                errors.push(new ExprError.InvalidArgument(DateDiffFn.Name, DateDiffFn.Signature, `expected <datetime> to be an instance of either DATETIME or TIMESTAMP but received ${Types.getResolvedType(arg)}`, callNode.location));
                        }
                        return errors;
                    },
                },
            ],
        };
        return validateArguments(sig, args);
    }
    DateDiffFn.validate = validate;
})(DateDiffFn || (DateDiffFn = {}));
